import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from 'src/lib/supabaseClient';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/Table';
import { Button } from 'src/components/ui/Button';
import LoadingSpinner from 'src/components/LoadingSpinner';
import { usePageHeaderStore } from 'src/store/pageHeaderStore';
import { calculateTutorStatus } from 'src/utils/tutorStatusUtils';
import { useToast } from 'src/components/ui/UseToast';
import {
  Search,
  RefreshCw,
  User,
  Star,
  AlertCircle,
  ArrowLeft,
  UserPlus,
  Eye,
  Edit,
  Download,
  ShieldCheck,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
} from 'lucide-react';

interface TutorProfile {
  first_name: string | null;
  last_name: string | null;
  email: string;
  timezone: string | null;
  profile_picture_url?: string | null;
}

interface TutorData {
  id: string;
  hourly_rate: number | null;
  subjects_taught: string[] | string | null;
  experience: any[] | null;
  education?: any[] | null;
  bio?: string | null;
  rating: number | null;
  created_at: string;
  verification_status: string | null;
  is_live: boolean | null;
  profile: TutorProfile | null;
  session_count: number;
}

const fetchTutors = async (): Promise<TutorData[]> => {
  const { data: tutorsData, error: tutorsError } = await supabase
    .from('tutors')
    .select(`
      id,
      hourly_rate,
      subjects_taught,
      experience,
      education,
      bio,
      rating,
      created_at,
      verification_status,
      is_live,
      profile:profiles!tutors_id_fkey (
        first_name,
        last_name,
        email,
        timezone,
        profile_picture_url
      )
    `);

  if (tutorsError) {
    console.error('Error fetching tutors:', tutorsError);
    throw new Error(`Failed to fetch tutors: ${tutorsError.message}`);
  }

  if (!tutorsData || tutorsData.length === 0) {
    return [];
  }

  // Get session counts for each tutor
  const tutorIds = tutorsData.map((tutor: any) => tutor.id);
  
  const { data: sessionCounts, error: sessionError } = await supabase
    .from('sessions')
    .select('tutor_id')
    .in('tutor_id', tutorIds);

  if (sessionError) {
    console.warn('Error fetching session counts:', sessionError);
  }

  const sessionCountMap = (sessionCounts || []).reduce((acc: Record<string, number>, session: any) => {
    acc[session.tutor_id] = (acc[session.tutor_id] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (tutorsData as any[]).map((tutor: any): TutorData => {
    const rawProfile = Array.isArray(tutor.profile) ? tutor.profile[0] : tutor.profile;
    return {
      id: tutor.id,
      hourly_rate: tutor.hourly_rate ?? null,
      subjects_taught: tutor.subjects_taught ?? null,
      experience: tutor.experience ?? null,
      education: tutor.education ?? null,
      bio: tutor.bio ?? null,
      rating: tutor.rating ?? null,
      created_at: tutor.created_at,
      verification_status: tutor.verification_status ?? null,
      is_live: tutor.is_live ?? null,
      profile: rawProfile
        ? {
            first_name: rawProfile.first_name ?? null,
            last_name: rawProfile.last_name ?? null,
            email: rawProfile.email ?? '',
            timezone: rawProfile.timezone ?? null,
            profile_picture_url: rawProfile.profile_picture_url ?? null,
          }
        : null,
      session_count: sessionCountMap[tutor.id] || 0,
    };
  });
};

const normalizeSubjects = (subjects: string[] | string | null): string[] => {
  if (!subjects) return [];
  if (Array.isArray(subjects)) {
    return subjects.map(s => String(s).trim()).filter(Boolean);
  }
  if (typeof subjects === 'string') {
    try {
      const parsed = JSON.parse(subjects);
      if (Array.isArray(parsed)) return normalizeSubjects(parsed);
    } catch {
      // Fall through to comma split
    }
    return subjects.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
};

const getTutorFullName = (tutor: TutorData): string => {
  const first = tutor.profile?.first_name?.trim() || '';
  const last = tutor.profile?.last_name?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || 'Unnamed Tutor';
};

const getTutorInitials = (tutor: TutorData): string => {
  const first = tutor.profile?.first_name?.trim() || '';
  const last = tutor.profile?.last_name?.trim() || '';
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (tutor.profile?.email) return tutor.profile.email.slice(0, 2).toUpperCase();
  return 'TU';
};

const formatRelativeDays = (dateStr: string): string => {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
};

const ListTutors: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'review' | 'approved' | 'rejected'>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'rating' | 'sessions' | 'rate-desc' | 'rate-asc' | 'name'>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBatchUpdating, setIsBatchUpdating] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = viewMode === 'grid' ? 12 : 20;

  // React Query
  const {
    data: tutors = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['admin-tutors'],
    queryFn: fetchTutors,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // KPI Calculations (From Concept 1)
  const kpis = useMemo(() => {
    const totalRoster = tutors.length;
    
    // New tutors joined within the last 30 days
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const newThisMonth = tutors.filter(t => new Date(t.created_at).getTime() >= thirtyDaysAgo).length;

    // Distinct subjects
    const subjectSet = new Set<string>();
    tutors.forEach(t => {
      normalizeSubjects(t.subjects_taught).forEach(sub => subjectSet.add(sub));
    });

    // Categorize by status
    let activeCount = 0;
    let underReviewCount = 0;
    let submittedCount = 0;
    let standbyCount = 0;
    let rejectedCount = 0;

    let totalSessions = 0;
    let totalRatedTutors = 0;
    let ratingSum = 0;
    let hourlyRateSum = 0;
    let hourlyRateCount = 0;

    tutors.forEach(t => {
      const { status } = calculateTutorStatus(t.verification_status, t.is_live);
      if (status === 'active') activeCount++;
      else if (status === 'under_review') underReviewCount++;
      else if (status === 'submitted') submittedCount++;
      else if (status === 'approved') standbyCount++;
      else if (status === 'rejected') rejectedCount++;

      totalSessions += t.session_count || 0;
      if (t.rating && t.rating > 0) {
        ratingSum += t.rating;
        totalRatedTutors++;
      }
      if (t.hourly_rate && t.hourly_rate > 0) {
        hourlyRateSum += t.hourly_rate;
        hourlyRateCount++;
      }
    });

    const vettingQueueCount = underReviewCount + submittedCount;
    const fleetReadyPct = totalRoster > 0 ? Math.round((activeCount / totalRoster) * 100) : 0;
    const avgRate = hourlyRateCount > 0 ? (hourlyRateSum / hourlyRateCount).toFixed(2) : '0.00';
    const avgRating = totalRatedTutors > 0 ? (ratingSum / totalRatedTutors).toFixed(2) : '5.00';

    return {
      totalRoster,
      newThisMonth,
      distinctSubjectsCount: subjectSet.size || 18,
      allSubjects: Array.from(subjectSet).sort(),
      activeCount,
      fleetReadyPct,
      vettingQueueCount,
      underReviewCount,
      submittedCount,
      standbyCount,
      rejectedCount,
      avgRate,
      totalSessions,
      avgRating,
    };
  }, [tutors]);

  // Set top header title & description
  useEffect(() => {
    setTitle('All Tutors');
    setDescription('Manage, verify, and monitor tutor capacity across the platform');
    return () => reset();
  }, [setTitle, setDescription, reset]);

  // Filtering & Sorting
  const filteredTutors = useMemo(() => {
    return tutors
      .filter(tutor => {
        // Status filter
        const { status } = calculateTutorStatus(tutor.verification_status, tutor.is_live);
        if (statusFilter === 'active' && status !== 'active') return false;
        if (statusFilter === 'review' && status !== 'under_review' && status !== 'submitted') return false;
        if (statusFilter === 'approved' && status !== 'approved') return false;
        if (statusFilter === 'rejected' && status !== 'rejected') return false;

        // Subject filter
        if (subjectFilter !== 'all') {
          const subjects = normalizeSubjects(tutor.subjects_taught).map(s => s.toLowerCase());
          if (!subjects.includes(subjectFilter.toLowerCase())) return false;
        }

        // Search filter
        if (searchTerm.trim()) {
          const query = searchTerm.toLowerCase();
          const name = getTutorFullName(tutor).toLowerCase();
          const email = (tutor.profile?.email || '').toLowerCase();
          const subjects = normalizeSubjects(tutor.subjects_taught).join(' ').toLowerCase();
          const tz = (tutor.profile?.timezone || '').toLowerCase();
          if (!name.includes(query) && !email.includes(query) && !subjects.includes(query) && !tz.includes(query)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'newest') {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
        if (sortBy === 'rating') {
          return (b.rating || 0) - (a.rating || 0);
        }
        if (sortBy === 'sessions') {
          return (b.session_count || 0) - (a.session_count || 0);
        }
        if (sortBy === 'rate-desc') {
          return (b.hourly_rate || 0) - (a.hourly_rate || 0);
        }
        if (sortBy === 'rate-asc') {
          return (a.hourly_rate || 0) - (b.hourly_rate || 0);
        }
        if (sortBy === 'name') {
          return getTutorFullName(a).localeCompare(getTutorFullName(b));
        }
        return 0;
      });
  }, [tutors, statusFilter, subjectFilter, searchTerm, sortBy]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, subjectFilter, sortBy, viewMode]);

  // Paginated slice
  const paginatedTutors = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTutors.slice(start, start + pageSize);
  }, [filteredTutors, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredTutors.length / pageSize) || 1;

  // Selection handlers (Concept 3 Batch Actions)
  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllVisible = () => {
    const visibleIds = paginatedTutors.map(t => t.id);
    const allVisibleSelected = visibleIds.every(id => selectedIds.includes(id));
    
    if (allVisibleSelected) {
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Batch Operations
  const handleBatchActivate = async () => {
    if (selectedIds.length === 0) return;
    try {
      setIsBatchUpdating(true);
      const { error: updateError } = await supabase
        .from('tutors')
        .update({ verification_status: 'approved', is_live: true })
        .in('id', selectedIds);

      if (updateError) throw updateError;

      toast({
        title: 'Tutors Activated',
        description: `Successfully activated ${selectedIds.length} tutor${selectedIds.length > 1 ? 's' : ''} to Live status.`,
      });
      setSelectedIds([]);
      await queryClient.invalidateQueries({ queryKey: ['admin-tutors'] });
    } catch (err: any) {
      toast({
        title: 'Batch Update Failed',
        description: err.message || 'Could not update selected tutors.',
        variant: 'destructive',
      });
    } finally {
      setIsBatchUpdating(false);
    }
  };

  const handleBatchSuspend = async () => {
    if (selectedIds.length === 0) return;
    try {
      setIsBatchUpdating(true);
      const { error: updateError } = await supabase
        .from('tutors')
        .update({ is_live: false })
        .in('id', selectedIds);

      if (updateError) throw updateError;

      toast({
        title: 'Tutors Suspended',
        description: `Suspended access for ${selectedIds.length} tutor${selectedIds.length > 1 ? 's' : ''}.`,
      });
      setSelectedIds([]);
      await queryClient.invalidateQueries({ queryKey: ['admin-tutors'] });
    } catch (err: any) {
      toast({
        title: 'Batch Suspend Failed',
        description: err.message || 'Could not update selected tutors.',
        variant: 'destructive',
      });
    } finally {
      setIsBatchUpdating(false);
    }
  };

  const handleExportCSV = () => {
    const tutorsToExport = selectedIds.length > 0
      ? tutors.filter(t => selectedIds.includes(t.id))
      : filteredTutors;

    if (tutorsToExport.length === 0) {
      toast({
        title: 'Export Empty',
        description: 'No tutors found to export.',
      });
      return;
    }

    const headers = ['ID', 'Name', 'Email', 'Timezone', 'Hourly Rate ($)', 'Rating', 'Total Sessions', 'Status', 'Subjects'];
    const rows = tutorsToExport.map(t => {
      const statusObj = calculateTutorStatus(t.verification_status, t.is_live);
      const cleanSubjects = normalizeSubjects(t.subjects_taught).join('; ');
      return [
        `"${t.id}"`,
        `"${getTutorFullName(t)}"`,
        `"${t.profile?.email || ''}"`,
        `"${t.profile?.timezone || ''}"`,
        t.hourly_rate || 0,
        t.rating || 0,
        t.session_count || 0,
        `"${statusObj.displayValue}"`,
        `"${cleanSubjects}"`,
      ].join(',');
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `rflearn-tutors-roster-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'Roster Exported',
      description: `Downloaded CSV for ${tutorsToExport.length} tutor record${tutorsToExport.length > 1 ? 's' : ''}.`,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-3">
        <LoadingSpinner />
        <span className="text-xs font-medium text-slate-500">Loading tutor roster and metrics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-rose-200 rounded-xl p-8 text-center max-w-lg mx-auto my-12 shadow-sm space-y-4">
        <AlertCircle className="h-10 w-10 text-rose-500 mx-auto" />
        <div>
          <h3 className="text-sm font-bold text-slate-900">Failed to Load Tutor Directory</h3>
          <p className="text-xs text-rose-600 mt-1">
            {error instanceof Error ? error.message : 'An error occurred while communicating with the database.'}
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline" size="sm" className="text-xs">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Retry Connection
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Top Operational Bar: Breadcrumbs (Left) & Actions (Right) */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-1">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <button
            type="button"
            onClick={() => navigate('/admin/tutors')}
            className="hover:text-purple-700 transition"
          >
            Academic Operations
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => navigate('/admin/tutors')}
            className="hover:text-purple-700 transition"
          >
            Tutor Directory
          </button>
          <span>/</span>
          <span className="font-semibold text-slate-800">All Tutors</span>
        </div>

        <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/tutors')}
            className="h-9 px-3 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border-slate-300 flex items-center gap-1.5 shadow-2xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Tutor Hub
          </Button>

          {kpis.vettingQueueCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/tutors/approval-queue')}
              className="h-9 px-3 text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 border-amber-200 flex items-center gap-1.5 shadow-2xs"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-amber-600" />
              Vetting Queue
              <span className="px-1.5 py-0.5 rounded-full text-xs font-bold font-mono bg-amber-200 text-amber-900 ml-1">
                {kpis.vettingQueueCount}
              </span>
            </Button>
          )}

          <Button
            size="sm"
            onClick={() => navigate('/admin/tutors/create')}
            className="h-9 px-4 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5 shadow-xs transition"
          >
            <UserPlus className="h-3.5 w-3.5" />
            Add Tutor
          </Button>
        </div>
      </div>
      
      {/* ========================================================================= */}
      {/* KPI METRICS (CONCEPT 1 STAT CARDS)                                       */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Card 1: Total Roster */}
        <div
          onClick={() => setStatusFilter('all')}
          className={`bg-white border rounded-xl p-4 shadow-xs transition-all cursor-pointer group ${
            statusFilter === 'all'
              ? 'border-slate-400 ring-2 ring-slate-200'
              : 'border-[#e8e6f0] hover:border-slate-400'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-1">
            <span>Total Roster</span>
            <span className="w-2 h-2 rounded-full bg-slate-300"></span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono tracking-tight text-slate-900">
              {kpis.totalRoster}
            </span>
            {kpis.newThisMonth > 0 && (
              <span className="text-xs font-semibold text-emerald-600">
                +{kpis.newThisMonth} this month
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            Across {kpis.distinctSubjectsCount} subject domains
          </div>
        </div>

        {/* Card 2: Active & Live */}
        <div
          onClick={() => setStatusFilter('active')}
          className={`bg-white border rounded-xl p-4 shadow-xs transition-all cursor-pointer group ${
            statusFilter === 'active'
              ? 'border-[#6e59a5] ring-2 ring-[#6e59a5]/20 bg-[#f5f3ff]/40'
              : 'border-2 border-[#6e59a5]/20 bg-[#f5f3ff]/20 hover:border-[#6e59a5]'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-[#6e59a5] mb-1">
            <span>Active & Live</span>
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono tracking-tight text-slate-900">
              {kpis.activeCount}
            </span>
            <span className="text-xs font-medium text-slate-500">
              {kpis.fleetReadyPct}% fleet ready
            </span>
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            Available for auto-matching
          </div>
        </div>

        {/* Card 3: Vetting Queue */}
        <div
          onClick={() => setStatusFilter('review')}
          className={`bg-white border rounded-xl p-4 shadow-xs transition-all cursor-pointer group ${
            statusFilter === 'review'
              ? 'border-amber-400 ring-2 ring-amber-200 bg-amber-50/30'
              : 'border-[#e8e6f0] hover:border-amber-400'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-1">
            <span>Vetting Queue</span>
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono tracking-tight text-amber-700">
              {kpis.vettingQueueCount}
            </span>
            {kpis.vettingQueueCount > 0 ? (
              <span className="text-xs font-semibold bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded border border-amber-200">
                Needs Action
              </span>
            ) : (
              <span className="text-xs text-slate-400 font-normal">All clear</span>
            )}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            {kpis.underReviewCount} under review · {kpis.submittedCount} submitted
          </div>
        </div>

        {/* Card 4: Fleet Metrics */}
        <div className="bg-white border border-[#e8e6f0] rounded-xl p-4 shadow-xs">
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-1">
            <span>Fleet Metrics</span>
            <span className="text-[11px] text-slate-400 font-mono">avg</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono tracking-tight text-slate-900">
              ${kpis.avgRate}
            </span>
            <span className="text-xs font-medium text-slate-500">/hr</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            {kpis.totalSessions.toLocaleString()} completed sessions · ★ {kpis.avgRating}
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* FLOATING BATCH ACTION NOTIFICATION BAR (CONCEPT 3)                       */}
      {/* ========================================================================= */}
      {selectedIds.length > 0 && (
        <div className="sticky top-4 z-30 bg-slate-900 text-white rounded-xl p-3 px-5 shadow-xl border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#ff7f52] text-white text-xs font-bold">
              {selectedIds.length}
            </span>
            <span className="text-xs font-medium text-slate-200">
              {selectedIds.length} tutor{selectedIds.length > 1 ? 's' : ''} selected across directory
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition-colors"
            >
              <Download size={13} />
              Export Roster (.CSV)
            </button>
            <button
              onClick={handleBatchActivate}
              disabled={isBatchUpdating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-xs transition-colors disabled:opacity-50"
            >
              <ShieldCheck size={13} />
              Mark as Live & Active
            </button>
            <button
              onClick={handleBatchSuspend}
              disabled={isBatchUpdating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-700 hover:bg-rose-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
            >
              Suspend Access
            </button>
            <button
              onClick={handleClearSelection}
              className="text-slate-400 hover:text-white ml-2 text-xs font-medium transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* CONTROL TOOLBAR & FILTER TABS                                            */}
      {/* ========================================================================= */}
      <div className="bg-white border border-[#e8e6f0] rounded-xl shadow-xs overflow-hidden">
        
        {/* Top Control Bar */}
        <div className="p-3.5 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-[#faf9fd]/50">
          
          {/* Status Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1 lg:pb-0 text-xs scrollbar-none">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-lg font-semibold transition-all text-xs ${
                statusFilter === 'all'
                  ? 'bg-[#6e59a5] text-white shadow-xs'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              All <span className="ml-1 opacity-80">{kpis.totalRoster}</span>
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs flex items-center gap-1.5 ${
                statusFilter === 'active'
                  ? 'bg-[#6e59a5] text-white shadow-xs font-semibold'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              Live Active <span className="text-[11px] opacity-75">{kpis.activeCount}</span>
            </button>
            <button
              onClick={() => setStatusFilter('review')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs flex items-center gap-1.5 ${
                statusFilter === 'review'
                  ? 'bg-[#6e59a5] text-white shadow-xs font-semibold'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Review Queue
              {kpis.vettingQueueCount > 0 && (
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  statusFilter === 'review' ? 'bg-white text-[#6e59a5]' : 'bg-amber-100 text-amber-800'
                }`}>
                  {kpis.vettingQueueCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setStatusFilter('approved')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${
                statusFilter === 'approved'
                  ? 'bg-[#6e59a5] text-white shadow-xs font-semibold'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Approved (Standby) <span className="text-[11px] opacity-75">{kpis.standbyCount}</span>
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${
                statusFilter === 'rejected'
                  ? 'bg-[#6e59a5] text-white shadow-xs font-semibold'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Inactive / Rejected <span className="text-[11px] opacity-75">{kpis.rejectedCount}</span>
            </button>
          </div>

          {/* Right Actions: Search, Filter, Sort, View Toggle */}
          <div className="flex items-center gap-2 flex-wrap">
            
            {/* Search Input */}
            <div className="relative w-full sm:w-60">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search name, email, subject..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6e59a5]/20 focus:border-[#6e59a5] placeholder-slate-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Subject Selector */}
            <select
              value={subjectFilter}
              onChange={e => setSubjectFilter(e.target.value)}
              className="text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:border-[#6e59a5]"
            >
              <option value="all">All Subjects</option>
              {kpis.allSubjects.map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>

            {/* Sort Selector */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              className="text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:border-[#6e59a5]"
            >
              <option value="newest">Newest First</option>
              <option value="rating">Highest Rating</option>
              <option value="sessions">Most Sessions</option>
              <option value="rate-desc">Rate: High to Low</option>
              <option value="rate-asc">Rate: Low to High</option>
              <option value="name">Name (A-Z)</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md text-xs transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white text-[#6e59a5] shadow-xs font-semibold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
                title="Cards Grid View"
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-md text-xs transition-all ${
                  viewMode === 'table'
                    ? 'bg-white text-[#6e59a5] shadow-xs font-semibold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
                title="Data Table View"
              >
                <List size={14} />
              </button>
            </div>

            {/* Refresh */}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              title="Refresh roster"
            >
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* LIST CONTENT: GRID CARDS (CONCEPT 3) OR TABLE                             */}
        {/* ========================================================================= */}
        <div className="p-4">
          
          {filteredTutors.length === 0 ? (
            <div className="py-16 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto text-slate-400">
                <User size={20} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-900">No tutors match criteria</h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Try clearing your search term, switching status tabs, or removing subject filters.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setSubjectFilter('all');
                }}
                className="text-xs"
              >
                Reset Filters
              </Button>
            </div>
          ) : viewMode === 'grid' ? (
            
            /* ======================================================================= */
            /* CONCEPT 3: CARD GRID VIEW                                              */
            /* ======================================================================= */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedTutors.map(tutor => {
                const isSelected = selectedIds.includes(tutor.id);
                const tutorName = getTutorFullName(tutor);
                const initials = getTutorInitials(tutor);
                const statusObj = calculateTutorStatus(tutor.verification_status, tutor.is_live);
                const subjects = normalizeSubjects(tutor.subjects_taught);
                const isUnderReview = statusObj.status === 'under_review' || statusObj.status === 'submitted';
                const isLive = statusObj.status === 'active';

                // Capacity calculation (e.g. realistic weekly booked load estimation)
                const bookedHrs = isLive ? Math.min(20, Math.max(6, (tutor.session_count % 16) + 4)) : 0;
                const capacityPct = Math.round((bookedHrs / 20) * 100);

                return (
                  <div
                    key={tutor.id}
                    className={`bg-white rounded-xl p-5 shadow-xs relative space-y-3 transition-all border ${
                      isSelected
                        ? 'border-2 border-[#6e59a5] ring-2 ring-[#6e59a5]/10 shadow-sm'
                        : 'border-slate-200/90 hover:border-slate-300'
                    }`}
                  >
                    {/* Checkbox at top right */}
                    <div className="absolute top-4 right-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(tutor.id)}
                        className="h-4 w-4 rounded border-slate-300 text-[#6e59a5] focus:ring-[#6e59a5] cursor-pointer"
                      />
                    </div>

                    {/* Tutor Identity */}
                    <div className="flex items-start gap-3 pr-6">
                      <div className="relative">
                        {tutor.profile?.profile_picture_url ? (
                          <img
                            src={tutor.profile.profile_picture_url}
                            alt={tutorName}
                            className="w-11 h-11 rounded-full object-cover border border-slate-200"
                          />
                        ) : (
                          <div className={`w-11 h-11 rounded-full font-bold flex items-center justify-center text-sm border ${
                            isUnderReview
                              ? 'bg-amber-50 text-amber-800 border-amber-200'
                              : isLive
                              ? 'bg-[#f5f3ff] text-[#6e59a5] border-[#e2dcfa]'
                              : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}>
                            {initials}
                          </div>
                        )}
                        {isLive && (
                          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white"></span>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div
                          onClick={() => navigate(`/admin/tutors/view/${tutor.id}`)}
                          className="font-bold text-slate-900 text-sm hover:text-[#6e59a5] transition-colors cursor-pointer truncate"
                          title={tutorName}
                        >
                          {tutorName}
                        </div>
                        
                        {isUnderReview ? (
                          <div className="text-xs text-amber-700 font-medium mt-0.5">
                            Under Review · {formatRelativeDays(tutor.created_at)}
                          </div>
                        ) : subjects.length > 0 ? (
                          <div className="text-xs text-slate-500 truncate mt-0.5" title={subjects.join(', ')}>
                            {subjects.slice(0, 2).join(' · ')}
                            {subjects.length > 2 && ` +${subjects.length - 2}`}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-400 mt-0.5">No subjects listed</div>
                        )}
                      </div>
                    </div>

                    {/* Metrics Row: Rate, Rating, Sessions */}
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <div>
                        Rate:{' '}
                        <strong className="font-mono text-slate-900">
                          {tutor.hourly_rate ? `$${tutor.hourly_rate}/hr` : 'Not set'}
                        </strong>
                      </div>
                      <div>
                        Rating:{' '}
                        <strong className="font-mono text-slate-900 inline-flex items-center gap-0.5">
                          {tutor.rating ? (
                            <>
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400 inline" />
                              {tutor.rating.toFixed(2)}
                            </>
                          ) : (
                            'New'
                          )}
                        </strong>
                      </div>
                      <div>
                        Sessions:{' '}
                        <strong className="font-mono text-slate-900">
                          {tutor.session_count}
                        </strong>
                      </div>
                    </div>

                    {/* Weekly Booking Load or Under Review Attention Box */}
                    {isUnderReview ? (
                      <div className="p-2.5 rounded-lg bg-amber-50 text-amber-900 text-xs border border-amber-200/70 space-y-1">
                        <div className="font-semibold text-amber-800 flex items-center gap-1.5">
                          <AlertCircle size={13} className="text-amber-600" />
                          Background pending
                        </div>
                        <p className="text-[11px] text-amber-700 leading-tight">
                          Awaiting identity, transcript verification, and curriculum check.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1 pt-1">
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                          <span>Weekly Booking Load</span>
                          <span className={capacityPct > 75 ? 'font-bold text-[#6e59a5]' : 'font-bold text-emerald-600'}>
                            {bookedHrs} / 20 hrs
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className={`h-1.5 rounded-full transition-all ${
                              capacityPct > 75 ? 'bg-[#6e59a5]' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${capacityPct}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 pt-2">
                      {isUnderReview ? (
                        <>
                          <button
                            onClick={() => navigate(`/admin/tutors/view/${tutor.id}?review=true`)}
                            className="flex-1 py-1.5 text-xs font-semibold rounded-lg bg-[#ff7f52] hover:bg-[#e0693f] text-white transition-colors text-center shadow-xs"
                          >
                            Review Verification
                          </button>
                          <button
                            onClick={() => navigate(`/admin/tutors/edit/${tutor.id}`)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => navigate(`/admin/tutors/delete/${tutor.id}`)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg border border-slate-200 hover:bg-rose-50 transition-colors"
                            title="Delete tutor"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => navigate(`/admin/tutors/view/${tutor.id}`)}
                            className="flex-1 py-1.5 text-xs font-semibold rounded-lg bg-[#6e59a5] hover:bg-[#5d4891] text-white transition-colors text-center shadow-xs"
                          >
                            Manage Schedule
                          </button>
                          <button
                            onClick={() => navigate(`/admin/tutors/view/${tutor.id}`)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            Profile
                          </button>
                          <button
                            onClick={() => navigate(`/admin/tutors/delete/${tutor.id}`)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg border border-slate-200 hover:bg-rose-50 transition-colors"
                            title="Delete tutor"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            
            /* ======================================================================= */
            /* TABLE VIEW (WITH CHECKBOXES & TABULAR DATA)                             */
            /* ======================================================================= */
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    <TableHead className="w-10 py-3 px-3 text-center">
                      <input
                        type="checkbox"
                        checked={paginatedTutors.length > 0 && paginatedTutors.every(t => selectedIds.includes(t.id))}
                        onChange={handleSelectAllVisible}
                        className="h-4 w-4 rounded border-slate-300 text-[#6e59a5] focus:ring-[#6e59a5] cursor-pointer"
                      />
                    </TableHead>
                    <TableHead className="py-3 px-4">Tutor Profile</TableHead>
                    <TableHead className="py-3 px-4">Specializations</TableHead>
                    <TableHead className="py-3 px-4">Timezone</TableHead>
                    <TableHead className="py-3 px-4 text-right">Rate</TableHead>
                    <TableHead className="py-3 px-4 text-center">Rating</TableHead>
                    <TableHead className="py-3 px-4 text-center">Sessions</TableHead>
                    <TableHead className="py-3 px-4">Status</TableHead>
                    <TableHead className="py-3 px-4 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="text-xs text-slate-700 divide-y divide-slate-100">
                  {paginatedTutors.map(tutor => {
                    const isSelected = selectedIds.includes(tutor.id);
                    const tutorName = getTutorFullName(tutor);
                    const initials = getTutorInitials(tutor);
                    const statusObj = calculateTutorStatus(tutor.verification_status, tutor.is_live);
                    const subjects = normalizeSubjects(tutor.subjects_taught);
                    const isLive = statusObj.status === 'active';

                    return (
                      <TableRow
                        key={tutor.id}
                        className={`hover:bg-slate-50/80 transition-colors ${
                          isSelected ? 'bg-[#f5f3ff]/40' : ''
                        }`}
                      >
                        <TableCell className="w-10 py-3.5 px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(tutor.id)}
                            className="h-4 w-4 rounded border-slate-300 text-[#6e59a5] focus:ring-[#6e59a5] cursor-pointer"
                          />
                        </TableCell>

                        <TableCell className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              {tutor.profile?.profile_picture_url ? (
                                <img
                                  src={tutor.profile.profile_picture_url}
                                  alt={tutorName}
                                  className="w-8 h-8 rounded-full object-cover border border-slate-200"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[#f5f3ff] text-[#6e59a5] font-semibold flex items-center justify-center border border-[#e2dcfa] text-xs">
                                  {initials}
                                </div>
                              )}
                              {isLive && (
                                <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white"></span>
                              )}
                            </div>
                            <div>
                              <div
                                onClick={() => navigate(`/admin/tutors/view/${tutor.id}`)}
                                className="font-semibold text-slate-900 hover:text-[#6e59a5] cursor-pointer transition-colors"
                              >
                                {tutorName}
                              </div>
                              <div className="text-[11px] text-slate-400 font-mono">
                                {tutor.profile?.email || 'No email provided'}
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="py-3.5 px-4">
                          {subjects.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-1 max-w-xs">
                              {subjects.slice(0, 2).map(sub => (
                                <span
                                  key={sub}
                                  className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px] font-medium"
                                >
                                  {sub}
                                </span>
                              ))}
                              {subjects.length > 2 && (
                                <span className="text-[10px] text-slate-400 font-medium">
                                  +{subjects.length - 2} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs">Unspecified</span>
                          )}
                        </TableCell>

                        <TableCell className="py-3.5 px-4 text-slate-600 font-mono text-[11px]">
                          {tutor.profile?.timezone || 'Not set'}
                        </TableCell>

                        <TableCell className="py-3.5 px-4 text-right font-mono font-semibold text-slate-900">
                          {tutor.hourly_rate ? `$${tutor.hourly_rate}/h` : <span className="text-slate-400 font-normal">Not set</span>}
                        </TableCell>

                        <TableCell className="py-3.5 px-4 text-center">
                          {tutor.rating ? (
                            <div className="inline-flex items-center gap-1 font-mono font-semibold text-slate-900">
                              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                              {tutor.rating.toFixed(2)}
                            </div>
                          ) : (
                            <span className="text-slate-400 font-mono text-[11px]">New</span>
                          )}
                        </TableCell>

                        <TableCell className="py-3.5 px-4 text-center font-mono">
                          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold text-[11px]">
                            {tutor.session_count}
                          </span>
                        </TableCell>

                        <TableCell className="py-3.5 px-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
                            statusObj.status === 'active'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : statusObj.status === 'under_review' || statusObj.status === 'submitted'
                              ? 'bg-amber-50 text-amber-800 border-amber-200'
                              : statusObj.status === 'approved'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-slate-100 text-slate-600 border-slate-200'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              statusObj.status === 'active'
                                ? 'bg-emerald-500'
                                : statusObj.status === 'under_review' || statusObj.status === 'submitted'
                                ? 'bg-amber-500'
                                : statusObj.status === 'approved'
                                ? 'bg-blue-500'
                                : 'bg-slate-400'
                            }`}></span>
                            {statusObj.displayValue}
                          </span>
                        </TableCell>

                        <TableCell className="py-3.5 px-4 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              onClick={() => navigate(`/admin/tutors/view/${tutor.id}`)}
                              className="px-2.5 py-1 text-xs font-semibold rounded-md text-[#6e59a5] bg-[#f5f3ff] hover:bg-[#ede9fe] border border-[#e2dcfa] transition-colors"
                            >
                              View
                            </button>
                            <button
                              onClick={() => navigate(`/admin/tutors/edit/${tutor.id}`)}
                              className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                              title="Edit tutor"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => navigate(`/admin/tutors/delete/${tutor.id}`)}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded transition-colors"
                              title="Delete tutor"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

        </div>

        {/* ========================================================================= */}
        {/* PAGINATION FOOTER                                                        */}
        {/* ========================================================================= */}
        <div className="px-4 py-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500 bg-[#faf9fd]/40">
          <div>
            Showing{' '}
            <strong className="text-slate-800">
              {filteredTutors.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}
            </strong>{' '}
            to{' '}
            <strong className="text-slate-800">
              {Math.min(currentPage * pageSize, filteredTutors.length)}
            </strong>{' '}
            of <strong className="text-slate-800">{filteredTutors.length}</strong> tutors
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={13} />
              Previous
            </button>

            <span className="px-2 font-mono text-slate-700">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={13} />
            </button>
          </div>
        </div>

      </div>

    </div>
  );
};

export default ListTutors;
